import React, { useState, useEffect } from 'react';
import { LatteButton } from '../common';
import '../../styles/common/latte-components.css';

const ImageSettingsModal = ({ isOpen, onClose, image, onSave }) => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [alignment, setAlignment] = useState('none');
  const [maintainRatio, setMaintainRatio] = useState(true);
  const [originalRatio, setOriginalRatio] = useState(1);

  useEffect(() => {
    if (image && isOpen) {
      const currentWidth = image.width || image.naturalWidth;
      const currentHeight = image.height || image.naturalHeight;
      
      setWidth(currentWidth);
      setHeight(currentHeight);
      setOriginalRatio(currentHeight / currentWidth);
      
      // 현재 정렬 상태 확인
      if (image.style.display === 'block' && image.style.margin === '0 auto') {
        setAlignment('center');
      } else if (image.style.float === 'right') {
        setAlignment('right');
      } else if (image.style.float === 'left') {
        setAlignment('left');
      } else {
        setAlignment('none');
      }
    }
  }, [image, isOpen]);

  const handleWidthChange = (value) => {
    setWidth(value);
    if (maintainRatio && value && !isNaN(value)) {
      setHeight(Math.round(value * originalRatio));
    }
  };

  const handleHeightChange = (value) => {
    setHeight(value);
    if (maintainRatio && value && !isNaN(value)) {
      setWidth(Math.round(value / originalRatio));
    }
  };

  const handleSave = () => {
    onSave({
      width: parseInt(width),
      height: parseInt(height),
      alignment
    });
    onClose();
  };

  const handleReset = () => {
    if (image) {
      setWidth(image.naturalWidth);
      setHeight(image.naturalHeight);
      setAlignment('none');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content image-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>이미지 설정</h3>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {/* 크기 설정 섹션 */}
          <div className="setting-section">
            <h4>크기 조절</h4>
            <div className="size-inputs">
              <div className="input-group">
                <label>너비 (px)</label>
                <input
                  type="number"
                  value={width}
                  onChange={(e) => handleWidthChange(e.target.value)}
                  min="1"
                  max="1200"
                />
              </div>
              <div className="input-group">
                <label>높이 (px)</label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => handleHeightChange(e.target.value)}
                  min="1"
                  max="1200"
                />
              </div>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={maintainRatio}
                onChange={(e) => setMaintainRatio(e.target.checked)}
              />
              <span>비율 유지</span>
            </label>
          </div>

          {/* 정렬 설정 섹션 */}
          <div className="setting-section">
            <h4>정렬</h4>
            <div className="alignment-options">
              <button
                type="button"
                className={`alignment-btn ${alignment === 'left' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setAlignment('left');
                }}
                title="왼쪽 정렬"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4 6h7m0 0v12m0-12h9m-9 4h9m-9 4h9" />
                </svg>
              </button>
              <button
                type="button"
                className={`alignment-btn ${alignment === 'center' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setAlignment('center');
                }}
                title="가운데 정렬"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4 6h16M4 12h16m-7-6v12" />
                </svg>
              </button>
              <button
                type="button"
                className={`alignment-btn ${alignment === 'right' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setAlignment('right');
                }}
                title="오른쪽 정렬"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M13 6h7m0 0v12m0-12H4m16-4H4m16 4H4" />
                </svg>
              </button>
              <button
                type="button"
                className={`alignment-btn ${alignment === 'none' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setAlignment('none');
                }}
                title="정렬 없음"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* 미리보기 */}
          {image && (
            <div className="preview-section">
              <h4>미리보기</h4>
              <div className="preview-container">
                <div className={`preview-image align-${alignment}`}>
                  <img 
                    src={image.src} 
                    alt="미리보기"
                    style={{
                      width: `${Math.min(width, 200)}px`,
                      height: `${Math.min(height, 200)}px`
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <LatteButton variant="outline" onClick={handleReset}>
            원본 크기로
          </LatteButton>
          <div className="footer-actions">
            <LatteButton variant="outline" onClick={onClose}>
              취소
            </LatteButton>
            <LatteButton variant="primary" onClick={handleSave}>
              적용
            </LatteButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageSettingsModal;
